import { parseJson, type StoragePort } from './storage';

export interface QueuePayloadDecoder<T> { decode(value: unknown): T | null; }

export interface UploadQueueItem<T> {
  readonly id: string;
  readonly payload: T;
  readonly queuedAt: number;
  readonly attempts: number;
  readonly lastAttemptAt?: number;
}

type UnknownRecord = Record<string, unknown>;

function record(value: unknown): UnknownRecord | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value) ? value as UnknownRecord : null;
}

export class PersistentUploadQueue<T> {
  private items = new Map<string, UploadQueueItem<T>>();

  constructor(
    private readonly storage: StoragePort,
    private readonly key: string,
    private readonly decoder: QueuePayloadDecoder<T>,
    private readonly now: () => number = Date.now,
  ) { this.restore(); }

  list(): readonly UploadQueueItem<T>[] {
    return [...this.items.values()].sort((left, right) => left.queuedAt - right.queuedAt);
  }

  enqueue(id: string, payload: T): void {
    if (!/^[A-Za-z0-9_-]{1,128}$/.test(id) || !this.decoder.decode(payload)) {
      throw new TypeError('Invalid upload queue item.');
    }
    const existing = this.items.get(id);
    if (existing && JSON.stringify(existing.payload) !== JSON.stringify(payload)) {
      throw new Error(`Upload id collision: ${id}`);
    }
    if (!existing) this.items.set(id, Object.freeze({ id, payload, queuedAt: this.now(), attempts: 0 }));
    this.persist();
  }

  attempted(id: string): void {
    const item = this.items.get(id);
    if (!item) return;
    this.items.set(id, Object.freeze({ ...item, attempts: item.attempts + 1, lastAttemptAt: this.now() }));
    this.persist();
  }

  acknowledge(id: string): void {
    if (this.items.delete(id)) this.persist();
  }

  private restore(): void {
    const envelope = record(parseJson(this.storage.getItem(this.key)));
    if (!envelope || envelope.version !== 2 || !Array.isArray(envelope.items)) {
      this.storage.removeItem(this.key);
      return;
    }
    for (const rawItem of envelope.items) {
      const item = record(rawItem);
      const id = typeof item?.id === 'string' && /^[A-Za-z0-9_-]{1,128}$/.test(item.id) ? item.id : null;
      const payload = this.decoder.decode(item?.payload);
      const queuedAt = Number.isSafeInteger(item?.queuedAt) && Number(item?.queuedAt) > 0 ? Number(item?.queuedAt) : null;
      const attempts = Number.isSafeInteger(item?.attempts) && Number(item?.attempts) >= 0 ? Number(item?.attempts) : null;
      const lastAttemptAt = Number.isSafeInteger(item?.lastAttemptAt) && Number(item?.lastAttemptAt) > 0
        ? Number(item?.lastAttemptAt) : undefined;
      if (id && payload && queuedAt !== null && attempts !== null) {
        this.items.set(id, Object.freeze({ id, payload, queuedAt, attempts, ...(lastAttemptAt ? { lastAttemptAt } : {}) }));
      }
    }
    this.persist();
  }

  private persist(): void {
    this.storage.setItem(this.key, JSON.stringify({ version: 2, items: this.list() }));
  }
}
